import { computed, defineComponent, h } from 'vue'

import { QSelect, QItem, QItemSection, QItemLabel, QAvatar, QImg } from 'quasar'

// Utils
import { slot } from '../utils'

import { bsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'
import { ellipsis } from '../../../utils'
import { quasarHandlers } from '../quasarHandlers'
import { useTemplateRef } from '../useTemplateRef'

/**
 * The app's dropdown. Options come in through `$attrs` rather than a prop, because the whole bag is
 * handed to QSelect.
 *
 * Two things it does that a QSelect does not: the selected option is left out of the menu (picking what
 * is already picked is a no-op), and the display value is truncated so a long book title cannot push the
 * control's width around.
 *
 * Option value `-1` is a "Please select..." placeholder, which is why `isComponentValid` rejects it.
 */
export default defineComponent({
  name: 'BsSelect',

  // See BsInput: an undeclared event that shares a DOM event's name is ALSO bound natively on the root,
  // so the caller's handler runs a second time with a DOM event. `input` comes from the composable,
  // `update:modelValue` beside it for `v-model`.
  emits: [ 'input', 'update:modelValue' ],

  props: {
    ...bsComponentProps,
    buttonOption: { type: Boolean, default: false },
    large: { type: Boolean, default: false },
    squareAvatar: { type: Boolean, default: false },
    fitAvatar: { type: Boolean, default: false },
    flat: { type: Boolean, default: false },
    popupContentClass: { type: String, default: void 0 },
    ellipsisCharCount: { type: Number, default: void 0 }
  },

  setup (props, { emit }) {
    // Template refs. A string `ref` in a render function does not populate `$refs` for a
    // component whose logic is in setup() -- see useTemplateRef.
    const { ref: bsSelect, set: setBsSelectRef } = useTemplateRef<any>()

    const vm = useVm()
    const bs = useBsComponent({
      vm,
      props,
      emit,
      autoSaveMethod: 'input',
      // Override this so we can allow for negative number options to be something like "Please select..."
      isComponentValid: (val?: any) => val !== null && val !== '' && val !== void 0 && val > -1
    })

    const getPopupContentClass = computed(() => {
      let classes = 'bs-select-options'

      if (props.flat) {
        classes = classes + '__flat'
      } else if (props.buttonOption) {
        classes += ' bs-select-options--button-enabled'
      }

      if (props.popupContentClass) {
        classes += ` ${props.popupContentClass}`
      }

      return classes
    })

    const backgroundColor = computed(() => {
      if (bs.hasErrors.value) {
        return 'imp-bs-r-l'
      }

      return vm.$attrs['bg-color']
    })

    return {
      bsSelect,
      setBsSelectRef,
      ...bs,
      getPopupContentClass,
      backgroundColor
    }
  },

  /**
   * `data-model` onto the field's ROOT, by hand, because there is no declarative way left to put it
   * there.
   *
   * Quasar 1 applied whatever it was handed to the field's root element, so this attribute — which
   * `cy.component()` looks up 1088 times, and which BsTabStepper reads to find the step holding an
   * invalid field — sat on something visible and clickable. Quasar 2's QSelect sets
   * `inheritAttrs: false` and routes attributes to its hidden `.q-select__focus-target` input, which
   * is `opacity: 0`: `should('be.visible')` fails on it, and so does anything else that needs a real
   * element. Moving it out to the container instead changed which box a click lands in and broke a
   * different test, so the honest answer is to put it back exactly where Quasar 1 had it.
   *
   * `updated` as well as `mounted`: QSelect re-renders its root on validation state changes, and Vue
   * would not know to re-apply an attribute it never set.
   */
  mounted () {
    this.stampDataModel()
  },

  updated () {
    this.stampDataModel()
  },

  methods: {
    stampDataModel () {
      const root = (this.bsSelect as { $el?: Element } | undefined)?.$el

      if (root !== void 0 && root.setAttribute !== void 0 && this.dataModel !== void 0) {
        root.setAttribute('data-model', this.dataModel)
      }
    },

    /** Renamed from `__renderOptions`: Vue does not proxy setup/method keys beginning with `_`. */
    renderOptions (props: Record<string, any>) {
      // Don't render the currently selected option as another option to select.
      if (props.opt.value === this.componentValue) {
        return
      }

      return h(QItem, {
        // 'bs-select-option' was the staticClass beside the modifier object.
        class: ['bs-select-option', {
          'bs-select-option--large': this.large,
          'bs-select-option--fit-avatar': this.fitAvatar
        }],
        /**
         * `itemProps` is Quasar 2's whole option scope: `clickable`, `active`, the aria attributes AND
         * `onClick`, which is what selects the option. Quasar 1 handed the props and the events
         * separately (`props`/`itemEvents`), and spreading those names here found nothing at all — so
         * every option in every menu rendered, looked right, and did nothing when clicked. The sort
         * and filter controls silently stopped working, which the e2e suite reported as a list that
         * never re-read itself.
         */
        ...props.itemProps
      // A component takes slot functions, not a children array, and an absent avatar section has
      // to be filtered out -- Vue 2 dropped an undefined child, Vue 3 renders a comment for it.
      }, { default: () => [
        props.opt.avatar === void 0 || !this.large
          ? void 0
          : h(QItemSection, {
            avatar: true
          }, { default: () => [
            h(QAvatar, {
              class: 'on-left',
              square: this.squareAvatar
            }, { default: () => [
              h(QImg, {
                src: props.opt.avatar
              })
            ] })
          ] }),
        h(QItemSection, {}, { default: () => [
          // Slots are h()'s THIRD argument. Passed as a prop, `default` is just an unknown prop on
          // QItemLabel, which then renders an empty `.q-item__label` -- every option in the menu came
          // out blank, and the app looked like it had lost its translations.
          h(QItemLabel, null, { default: () => [props.opt.label] })
        ] })
      ].filter(child => child !== void 0) })
    },

    renderSelectedItem (props: Record<string, any>) {
      return h(QItem, {
        class: 'bs-selected-option',
        /**
         * No `itemProps` here: the `selected-item` scope is `{ index, opt, selected, removeAtIndex,
         * toggleOption, tabindex }` and carries no props or handlers of its own — in Quasar 1 either,
         * so the `props`/`itemEvents` spreads this used to do were already no-ops. The selected item is
         * a label, not a control; clicking it opens the menu, which QSelect handles itself.
         */
        clickable: true,
        dense: true
      }, { default: () => [
        props.opt.avatar === void 0
          ? void 0
          : h(QItemSection, {}, { default: () => [
            h(QAvatar, {
              class: 'on-left',
              square: this.squareAvatar
            }, { default: () => [
              h(QImg, {
                src: props.opt.avatar
              })
            ] })
          ] }),
        h(QItemSection, {}, { default: () => [
          // As in renderOptions: the slot is h()'s third argument, not a prop.
          h(QItemLabel, null, { default: () => [props.opt.label] })
        ] })
      ].filter(child => child !== void 0) })
    }
  },

  render () {
    const currentLabel = Array.isArray(this.$attrs['options'])
      ? Array.from(this.$attrs['options']).find(option => option.value === this.componentValue)?.label
      : '' // BsSelect would not work without options prop, using fallback value as a precaution

    const props = {
      mapOptions: true,
      emitValue: true,
      dropdownIcon: 'app:arrow-down',
      popupContentClass: this.getPopupContentClass,
      displayValue: ellipsis(currentLabel, this.ellipsisCharCount || 23),
      stackLabel: true,
      borderless: true,
      bottomSlots: true,
      noErrorIcon: true,
      filled: this.flat && !this.hasErrors,
      outlined: this.hasErrors,
      bgColor: this.backgroundColor,
      virtualScrollSliceSize: this.$attrs['options'].length,
      dense: true,
      // show menu regardless if it is desktop or mobile
      behavior: 'menu'
    }

    let scopedSlots = {}
    // Only if we're not trying to override from outside the component
    if (this.$slots.option === void 0) {
      scopedSlots = {
        option: (props: Record<string, any>) => this.renderOptions(props)
      }
    }

    // Smaller select doesn't support this.
    if (
      this.$slots.selectedItem === void 0 &&
      this.$slots['selected-item'] === void 0 &&
      this.large
    ) {
      scopedSlots = {
        ...scopedSlots,
        ['selected-item']: (props: Record<string, any>) => this.renderSelectedItem(props)
      }
    }


    return h('div', {
      class: 'bs-select-container',
      // NOTE, carried over: `id` is not a VNodeData field either, so Vue drops it and the container
      // never gets one. Pinned by the spec.
      id: this.dataModel
    }, [
      h(QSelect, {
        // 'bs-select' was the staticClass beside these.
        class: ['bs-select', this.getClasses({
          'bs-select--flat': this.flat,
          // `componentValue`, not `$attrs.value`: a caller binding `v-model` sends `modelValue`, so the
          // old name is undefined and this never matched.
          'bs-select--flat__default-option-showing': this.flat && parseInt(this.componentValue) === -1,
          'bs-select--large': this.large,
          'bs-select--fit-avatar': this.fitAvatar
        })],
        ref: this.setBsSelectRef,
        ...this.getRenderProps(props),
        ...quasarHandlers(this.getListeners({
          /**
           * `componentValue`, not `$attrs.value`. With `v-model` the value arrives as `modelValue`, so
           * this validated `undefined` on every blur and marked a perfectly valid select invalid —
           * which then blocked the wizard from stepping forward with "Please, fill in the correct
           * information" under a select that had a team chosen in it.
           */
          blur: () => this.validate(this.componentValue)
        })),
        // `true`: the container above carries `data-model` now — see its note.
        ...this.getAttributes(void 0, true)
      }, {
      ...this.getSlots(scopedSlots),
      // Vue 3 takes ONE slots object; what this passed as a fourth argument is the default slot.
      default: () => [slot(this, 'default')]
    })
    ])
  }
})
